import React from 'react';
import { useSearchParams } from 'react-router';

import { formatDate } from '@repo/util/formatDate';
import { ResultOf } from '@graphql-typed-document-node/core';

import { Tabs } from '@repo/ui/Tabs';
import { TabDataCard } from '@repo/ui/Tabs';
import { DataCardGroup } from '@repo/ui/Tabs';

import SMSES_SHOW_Q from '../gql/smsRequest_show.gql';

import { useUser } from '@repo/hooks/useUser';
import { POLICIES } from '@/lib/Authorization';

import SmsWithSegments from './SmsWithSegments';
import MessagesConversationsTab from './MessagesConversationsTab';
import type { Mercury } from '@repo/types/Models';

const CONVERSATION_TAB = 'Conversation';

export type SmsOverviewPropsData = ResultOf<typeof SMSES_SHOW_Q>;

export interface SmsOverviewProps {
  data: SmsOverviewPropsData;
  error?: Error | undefined;
  loading: boolean;
}

const SmsOverview = ({ data, error, loading }: SmsOverviewProps) => {
  const user = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'Overview';
  const setActiveTab = (next: string) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('tab', next);
        return params;
      },
      { replace: true }
    );
  };

  const SmsOverviewData: DataCardGroup[] = [
    {
      groupTitle: 'account',
      columnCount: 3,
      groupData: [
        {
          label: 'Account',
          entry: POLICIES['accounts:show'](user) ? (
            <a
              className="underline transition-colors hover:dark:text-blue-300"
              href={`/a/${data?.smsRequest.account.id}`}
            >
              {data?.smsRequest.account.name}
            </a>
          ) : (
            <span>{data?.smsRequest.account.name}</span>
          ),
        },
        {
          label: 'ID',
          entry: data?.smsRequest.id,
        },
        {
          label: 'Created at',
          entry: !loading && formatDate(data?.smsRequest.insertedAt),
        },
        {
          label: 'Sent at',
          entry: !loading && formatDate(data?.smsRequest.processedAt),
        },
        {
          label: 'Delivered at',
          entry: !loading && formatDate(data?.smsRequest.updatedAt),
        },
      ],
    },

    {
      groupTitle: 'status',
      columnCount: 3,
      groupData: [
        {
          label: 'Status',
          entry: data?.smsRequest.status,
        },

        {
          label: 'Status code',
          entry: data?.smsRequest.errorCode,
        },
      ],
    },

    {
      groupTitle: 'delivery',
      columnCount: 3,
      groupData: [
        {
          label: 'Direction',
          entry: data?.smsRequest.direction,
        },
        {
          label: 'Host number',
          entry: data?.smsRequest.hostNumber,
        },
        {
          label: 'Remote number',
          entry: data?.smsRequest.remoteNumber,
        },
        {
          label: 'Destination carrier',
          entry: data?.smsRequest.remoteReferenceId,
        },
      ],
    },

    {
      groupTitle: 'body',
      columnCount: 2,
      groupData: [
        {
          label: 'SMS Segment Body',
          entry: data?.smsRequest.displayBody,
        },
        {
          label: 'Full SMS Message',
          entry: (
            <SmsWithSegments
              primarySegment={data?.smsRequest as Mercury.Models.SMS}
              inDialog={false}
            />
          ),
        },
      ],
    },
  ];

  const SmsTabs = [
    {
      title: 'Overview',
      contents: <TabDataCard title="Overview" loading={loading} data={SmsOverviewData} />,
    },
    {
      title: CONVERSATION_TAB,
      contents: (
        <MessagesConversationsTab
          accountId={data?.smsRequest.account.id ?? ''}
          hostNumber={data?.smsRequest.hostNumber}
          remoteNumber={data?.smsRequest.remoteNumber}
          currentMessage={
            data?.smsRequest
              ? {
                  id: data.smsRequest.id,
                  type: 'SMS_REQUEST',
                  insertedAt: data.smsRequest.insertedAt,
                  direction: data.smsRequest.direction,
                  displayBody: data.smsRequest.displayBody,
                  status: data.smsRequest.status,
                }
              : undefined
          }
        />
      ),
    },
  ];

  return (
    <Tabs
      tabs={SmsTabs}
      loading={loading}
      error={error}
      value={activeTab}
      onValueChange={setActiveTab}
    />
  );
};

export default SmsOverview;
